/**
 * Het toestandsmodel van een potje.
 *
 * ## De indeling van een vorstendom
 *
 * Een vorstendom is een horizontale strook. In het midden staan de bouwwerken op een
 * rij; daarboven en daaronder liggen de landschappen, diagonaal ten opzichte van de
 * dorpen. Zo zag het er in het origineel uit bij de start:
 *
 * ```
 * kolom:   0     1     2     3     4
 * boven:  bos         weide       bergen
 * midden:       dorp  straat dorp
 * onder:  akker       rivier      heuvels
 * ```
 *
 * Daaruit volgt de hele meetkunde:
 * - **bouwwerken** staan op oneven kolommen (1, 3, 5, …), **straten** op even (2, 4, …)
 * - **landschappen** liggen op even kolommen, boven of onder
 * - een landschap op kolom `c` grenst aan de bouwwerken op kolom `c-1` en `c+1`;
 *   het buitenste landschap grenst er dus aan één, het middelste aan twee
 * - elk dorp heeft zo **4 aangrenzende landschappen** (2 boven, 2 onder), precies wat
 *   de tutorial zegt
 * - **uitbreidingen** komen op de vrije plaatsen recht boven en onder een bouwwerk:
 *   een dorp heeft er één boven en één onder, een stad twee boven en twee onder
 *
 * Bron: kennisbank 02 §1, handboek p. 10-14, en de referentie-installatie.
 */
import type { EventFace, Landscape, ProductionRoll, Resource, Seat } from '@catan/contracts';
import type { Rng } from './rng.js';

/** Boven of onder de bouwwerkenrij. Bepaalt welke uitbreiding op welk landschap werkt. */
export type Side = 'boven' | 'onder';

export interface LandscapeTile {
  readonly landscape: Landscape;
  /** Even kolom; grenst aan de bouwwerken op kolom−1 en kolom+1. */
  readonly column: number;
  readonly side: Side;
  /** Het getal waarop dit landschap opbrengst geeft (1-6). */
  readonly dieNumber: ProductionRoll;
  /** Aantal grondstoffen dat erop ligt, maximaal 3. */
  readonly resources: number;
}

export type BuildingKind = 'dorp' | 'stad';

export interface Building {
  readonly kind: BuildingKind;
  /** Oneven kolom. */
  readonly column: number;
  /**
   * Gebouwde uitbreidingen per zijde, in volgorde van binnen naar buiten.
   * Een dorp heeft één plaats per zijde, een stad twee.
   */
  readonly expansions: { readonly boven: readonly number[]; readonly onder: readonly number[] };
}

/** Een straat verbindt twee bouwwerken; hij staat op de even kolom ertussen. */
export interface Road {
  readonly column: number;
}

export interface Principality {
  readonly buildings: readonly Building[];
  readonly roads: readonly Road[];
  readonly landscapes: readonly LandscapeTile[];
}

export interface PlayerState {
  readonly seat: Seat;
  readonly name: string;
  readonly principality: Principality;
  /** Kaart-ID's in de hand. Alleen zichtbaar voor de speler zelf. */
  readonly hand: readonly number[];
  /** Grondstoffen liggen op de landschappen, niet in een aparte voorraad. */
  readonly handLimit: number;
}

/** Fase binnen een beurt. Volgt de beurtvolgorde uit kennisbank 02 §2. */
export type Phase =
  | 'opzet'
  | 'dobbelen'
  | 'gebeurtenis'
  | 'opbrengst'
  | 'hoofdfase'
  | 'aanvullen'
  | 'afgelopen';

/**
 * Waarop de kern wacht voordat de beurt verder kan.
 *
 * Drie gebeurtenissen vragen een keuze van een speler (goed jaar, riddertoernooi,
 * handelsvoordeel). De kern beslist die niet zelf: hij zet de vraag klaar en wacht.
 */
export type Pending =
  | {
      readonly kind: 'kies-opbrengst';
      readonly seat: Seat;
      readonly reason: 'goed-jaar' | 'riddertoernooi';
    }
  /**
   * Pak een grondstof weg bij de ander — en bij de Rooftocht méér dan één.
   *
   * `resterend` telt af: het handelsvoordeel steelt er 1, de Rooftocht 2 ("mag je naar
   * believen 2 grondstoffen stelen", p. 11 §5). Elke keuze opent een `plaats-buit`; zit
   * er dan nog een diefstal in het vat, dan draagt die de teller verder via `hierna`.
   */
  | { readonly kind: 'kies-buit'; readonly seat: Seat; readonly from: Seat; readonly resterend: number }
  | {
      readonly kind: 'plaats-buit';
      readonly seat: Seat;
      readonly resource: Resource;
      /** Ná het plaatsen nóg een keer stelen: bij wie, en hoeveel er nog mogen. */
      readonly hierna?: { readonly from: Seat; readonly resterend: number };
    }
  /**
   * Jij wijst aan wélke kaart je tegenstander terugneemt.
   *
   * Twee bronnen. De **Burgeroorlog** ("De speler die aan de beurt is, kiest als eerste de
   * ridder of vloot van de andere speler, die deze terug moet nemen", handboek p. 13 §13) en
   * een **mislukte aanval**: bij een 6 keert de Zwarte ridder of de Vuurduivel zich om, en
   * dan kiest de verdediger — "Bij 6 neem jij de ridder van zijn keuze terug".
   *
   * Vandaar `soort`: de Vuurduivel raakt gebouwen, en "vloten en ridders zijn geen gebouwen"
   * (p. 11 §2). Zonder dat onderscheid zou een mislukte Vuurduivel een ridder kosten.
   */
  | {
      readonly kind: 'kies-terugname';
      readonly seat: Seat;
      readonly from: Seat;
      readonly soort?: 'ridder-of-vloot' | 'gebouw';
    }
  /** Conflict: de houder van de ridderfiguur kiest kaarten uit de hand van de ander. */
  | { readonly kind: 'kies-handkaarten'; readonly seat: Seat; readonly from: Seat; readonly aantal: number }
  /**
   * Er komt een aanval op je af en je hebt de tegenkaart in de hand.
   *
   * Het origineel speelt die niet vanzelf uit: er komt een banner met de vraag of je hem
   * wilt inzetten. Zeg je ja, dan komt de tegenkaart óók groot in beeld en pas daarna
   * rolt de dobbelsteen die de aanval beslecht. Zeg je nee, dan houd je de kaart en loop
   * je het volle risico.
   */
  | {
      readonly kind: 'kies-tegenkaart';
      readonly seat: Seat;
      readonly attacker: Seat;
      /** De aanvalskaart die op je afkomt. */
      readonly attack: string;
      /** De tegenkaart die je in de hand hebt. */
      readonly counterId: number;
      /** Het doelwit dat de aanvaller koos, zodat de aanval erna verder kan. */
      readonly target: unknown;
    }
  /**
   * Je hebt te veel kaarten in de hand en moet het overtal onder een stapel leggen.
   *
   * Dit ontstaat niet door zelf te pakken — dat wordt tegengehouden — maar door
   * kaarteffecten: een Burgeroorlog of een Vuurduivel duwt een gebouwde uitbouw terug in
   * je hand, en een gesloopte Bibliotheek neemt de extra handplaats meteen mee.
   * Handboek msg 22.
   */
  | { readonly kind: 'kies-afleggen'; readonly seat: Seat }
  /**
   * Je hebt de kaart gekozen; nu wijs je aan onder welke stapel hij verdwijnt.
   *
   * Het origineel laat je dat echt zelf kiezen — je klikt links een van de vijf
   * uitbouwstapels aan. Er zelf een voor bepalen scheelt een klik maar neemt je een
   * beslissing af die je later nog kunt gebruiken: onder een stapel die je zelden pakt,
   * ligt de kaart veiliger.
   */
  | { readonly kind: 'kies-afleg-stapel'; readonly seat: Seat; readonly cardId: number }
  /**
   * Je hebt twee grondstoffen betaald om een uitbouwstapel te bekijken; kies er nu één
   * uit.
   *
   * Handboek p. 16: aan het eind van je beurt mag je de bovenste kaart gratis pakken,
   * óf twee grondstoffen betalen om de hele stapel door te kijken en vrij te kiezen. De
   * volgorde van de stapel blijft daarbij intact — er wordt niet geschud.
   */
  | { readonly kind: 'kies-uit-stapel'; readonly seat: Seat; readonly stack: number }
  /**
   * Zet je de Verkenner in bij het dorp dat je zojuist bouwde?
   *
   * "Deze kaart wordt meteen na de bouw van een nieuw dorp gespeeld" (handboek p. 46).
   * Vandaar dat het spel het vráágt in plaats van dat je hem uit je hand speelt: het
   * moment ligt vast, en wie hem niet inzet krijgt de twee bovenste van de stapel.
   */
  | { readonly kind: 'verkenner-vraag'; readonly seat: Seat; readonly column: number }
  /**
   * De landschappen uitzoeken met de Verkenner.
   *
   * "De speler mag alle kaarten van de landschapstapel bekijken en 2 kaarten uitkiezen."
   * De volgorde is de plaatsing: je kiest eerst het landschap dat bóven het dorp komt,
   * daarna dat eronder. Meer keuze is er niet — de twee plekken liggen vast op de
   * buitenhoeken van het nieuwe dorp.
   */
  | {
      readonly kind: 'verkenner-kies';
      readonly seat: Seat;
      /** Bij welk dorp de landschappen komen. */
      readonly column: number;
      /** Naar welke kant het vorstendom groeide. */
      readonly outward: 1 | -1;
      /** De kant die je nu vult. */
      readonly zijde: Side;
      /** Wat er al gekozen is, in volgorde. */
      readonly gekozen: readonly StartLandschap[];
    }
  /**
   * Je startkaarten kiezen uit een uitbouwstapel.
   *
   * Handboek p. 11: aan het begin kiest elke speler een stapel en neemt daar drie
   * kaarten uit. Zonder die drie begin je met een lege hand en kun je de eerste beurten
   * niets — precies wat er misging.
   */
  /**
   * De gebeurtenis **Bouwmeester**, in drie stappen per speler.
   *
   * "Iedere speler mag uit een uitbouwkaartenstapel een willekeurige handkaart uitkiezen
   * en geeft er één voor terug. De speler die aan de beurt is kiest eerst een stapel uit.
   * Zijn tegenstander moet een andere stapel kiezen." Vandaar `verboden`: de stapel die
   * de eerste speler nam, is voor de tweede geblokkeerd.
   */
  | {
      readonly kind: 'bouwmeester';
      readonly seat: Seat;
      /** Welke stap: een stapel kiezen, er een kaart uit pakken, of er een teruggeven. */
      readonly stap: 'stapel' | 'kaart' | 'afgeven';
      /** De gekozen stapel, zodra die er is. */
      readonly stack: number | undefined;
      /** De stapel die de ander al nam; die mag jij niet. */
      readonly verboden: number | undefined;
      /** Wie er hierna nog aan de beurt is voor deze gebeurtenis. */
      readonly hierna: Seat | undefined;
    }
  /**
   * De **Handelaar**: wijs aan welke grondstoffen je van je tegenstander afpakt.
   *
   * "Neem je tegenstander 2 grondstoffen **van je keuze** af." Dat "van je keuze" deed
   * het spel eerst zelf; nu wijs je ze een voor een aan, net als in het origineel.
   */
  | {
      readonly kind: 'handelaar';
      readonly seat: Seat;
      readonly from: Seat;
      readonly resterend: number;
    }
  /**
   * De teruggave van de Handelaar: "geef hem daar **1** grondstof van jouw keuze voor
   * terug" (p. 12 §9). De handelaar-speler wijst een eigen gevulde tegel aan; de
   * grondstof gaat naar de ander — mits die er plaats voor heeft, dezelfde regel als
   * bij het afpakken.
   */
  | { readonly kind: 'handelaar-terug'; readonly seat: Seat; readonly from: Seat }
  /**
   * De Karavaan: "verwijder 2 van je grondstoffen en neem er 2 van jouw keuze voor
   * terug" (p. 12 §10, met het voorbeeld "je draait je bos van 3 hout naar 1 hout terug
   * en je berg 1 erts en je akker 1 graan omhoog"). Eerst twee keer een eigen gevulde
   * tegel aanwijzen (afgeven), daarna twee keer een eigen tegel met ruimte (terugnemen).
   * De andere speler blijft er helemaal buiten.
   */
  | {
      readonly kind: 'karavaan';
      readonly seat: Seat;
      readonly stap: 'geef' | 'neem';
      readonly resterend: number;
    }
  /**
   * Een nieuw dorp is gebouwd en de getrokken landen liggen klaar.
   *
   * "De speler beslist zelf, welk land hij boven en welk land hij onder legt" (p. 9).
   * De keuze is één klik: de plek voor de éérste kaart; de tweede valt vanzelf aan de
   * andere kant. Bij een bijna lege stapel ligt er soms maar één kaart — dan kies je
   * alleen nog de kant.
   */
  | {
      readonly kind: 'leg-landschap';
      readonly seat: Seat;
      /** De buitenkolom naast het nieuwe dorp waar de landen komen. */
      readonly column: number;
      readonly kaarten: readonly StartLandschap[];
    }
  /**
   * De **Spion**: kies een kaart uit de hand van je tegenstander.
   *
   * "Kies uit de handkaarten van je tegenstander 1 kaart uit." Zolang deze vraag
   * openstaat mag je zijn hand zien — anders valt er niets te kiezen.
   */
  | { readonly kind: 'spion'; readonly seat: Seat; readonly from: Seat }
  | {
      readonly kind: 'kies-startkaarten';
      readonly seat: Seat;
      readonly stack: number;
      readonly resterend: number;
    };

export interface DiceState {
  readonly event: EventFace;
  readonly production: ProductionRoll;
}

export interface GameState {
  readonly rulesVersion: number;
  readonly cardsVersion: number;
  readonly rng: Rng;
  readonly players: readonly [PlayerState, PlayerState];
  /** Wie aan zet is. */
  readonly turn: Seat;
  /** Hoeveel volledige beurten er zijn gespeeld; nodig voor de roofoverval-vrijstelling. */
  readonly turnNumber: number;
  readonly phase: Phase;
  readonly dice: DiceState | undefined;
  /** Waarop de kern wacht: een keuze van een speler. Zolang dit gevuld is, staat de beurt stil. */
  readonly pending: Pending | undefined;
  /** De vijf gedeelde uitbouwstapels, elk een lijst kaart-ID's (bovenste eerst). */
  readonly stacks: readonly (readonly number[])[];
  /** De gebeurteniskaartenstapel. */
  readonly eventDeck: readonly number[];
  /** Aflegstapel. */
  readonly discard: readonly number[];
  /** Wie de speelfiguren heeft; undefined bij gelijkspel. */
  readonly knightToken: Seat | undefined;
  readonly windmillToken: Seat | undefined;
  readonly winner: Seat | undefined;
  /**
   * Door de Alchimist vastgezette uitkomst van de opbrengstendobbelsteen; geldt voor de
   * eerstvolgende worp.
   */
  readonly forcedProduction: ProductionRoll | undefined;
  /**
   * De landschapstapel: de kaarten die nog niet in een vorstendom liggen.
   *
   * Sticht je een dorp, dan komen daar twee landschappen bij; die komen hiervandaan, van
   * boven af. Met de **Verkenner** mag je in plaats daarvan de hele stapel bekijken en er
   * zelf twee uitzoeken (handboek p. 46) — zonder voorraad valt er niets te kiezen, dus
   * die stapel is de voorwaarde voor die kaart.
   */
  readonly landscapeDeck: readonly StartLandschap[];
  /** Een ruilaanbod dat op antwoord wacht. */
  readonly offer: OpenOffer | undefined;
  /**
   * De landschappen die nog geplaatst moeten worden, per speler.
   *
   * Kies je aan het begin "zelf verdelen", dan ligt je bord leeg en zitten je zes
   * landschappen hier — met hun dobbelgetal, want dat hoort bij het landschap en niet
   * bij de plek. Verdeelt het spel voor je, dan is deze lijst meteen leeg.
   */
  readonly setupPool: readonly [readonly StartLandschap[], readonly StartLandschap[]];
  /**
   * De gebeurteniskaart die zojuist getrokken is.
   *
   * Het scherm heeft die nodig om de kaart groot te tonen; het verslag alleen zegt niet
   * wélke plaat erbij hoort.
   */
  readonly lastEventCard: number | undefined;
  /**
   * Hoeveel ruilaanbiedingen elke speler deze beurt heeft gedaan.
   *
   * Het origineel laat de computer er hoogstens vijf doen (`23_aktiverHandel.ki`, rijen
   * 26-30). Zonder grens blijft hij hetzelfde voorstel herhalen zodra je het afwijst.
   */
  readonly offersThisTurn: readonly [number, number];
  /**
   * Al een ontwikkelingskaart geruild deze beurt?
   *
   * "Uitsluitend wanneer een speler in zijn beurt zijn hand niet mag aanvullen, mag hij
   * **één** ontwikkelingskaart ruilen" (p. 10). Zonder deze vlag kon je eindeloos rond:
   * hand vol → ruilen → pakken → hand vol → wéér ruilen. Oude momentopnamen missen het
   * veld; `undefined` telt als "nog niet geruild", en dat klopt ook.
   */
  readonly kaartGeruild?: boolean;
  /**
   * De aanbiedingen die deze beurt zijn afgewezen.
   *
   * "Nee" is een antwoord: hetzelfde voorstel nog eens doen is geen onderhandelen maar
   * zeuren. Zolang de beurt duurt staat een afgewezen bundel dus op slot — de aanbieder
   * moet met iets ánders komen. Bij het wisselen van beurt is de lei weer schoon, want
   * dan is de voorraad veranderd en kan hetzelfde voorstel opeens wél schikken.
   */
  readonly afgewezenAanbiedingen: readonly AfgewezenAanbod[];
  /**
   * Hoeveel grondstoffen elke speler via de **opbrengstendobbelsteen** heeft gekregen.
   * Alleen die tellen mee voor de eindscore (handboek p. 8): wat je ruilt of met
   * actiekaarten pakt telt niet.
   */
  readonly produced: readonly [number, number];
}

/** Een landschap dat nog een plek moet krijgen. */
export interface StartLandschap {
  readonly landscape: Landscape;
  readonly dieNumber: ProductionRoll;
}

/** Een ruilaanbod tussen spelers dat op antwoord wacht. */
export interface OpenOffer {
  readonly from: Seat;
  readonly give: Readonly<Partial<Record<Resource, number>>>;
  readonly want: Readonly<Partial<Record<Resource, number>>>;
}

/** Een aanbod waarop deze beurt al "nee" is gezegd. */
export interface AfgewezenAanbod {
  readonly from: Seat;
  readonly give: Readonly<Partial<Record<Resource, number>>>;
  readonly want: Readonly<Partial<Record<Resource, number>>>;
}

/**
 * Twee bundels zijn hetzelfde als er van elke grondstof evenveel in zit.
 *
 * Een bundel is een gewoon object, dus `{hout: 1}` en `{hout: 1, klei: undefined}` zijn
 * voor JavaScript verschillend maar voor het spel gelijk. Daarom tellen we per soort.
 */
export function zelfdeBundel(
  a: Readonly<Partial<Record<Resource, number>>>,
  b: Readonly<Partial<Record<Resource, number>>>,
): boolean {
  const soorten = new Set([...Object.keys(a), ...Object.keys(b)] as Resource[]);
  for (const soort of soorten) {
    if ((a[soort] ?? 0) !== (b[soort] ?? 0)) return false;
  }
  return true;
}

// ---------------------------------------------------------------- opslag

/**
 * De spelstand als platte JSON.
 *
 * De generatorstand is een 64-bits getal en dus een `bigint`; `JSON.stringify` kan
 * daar niet mee overweg. Omdat snapshots als JSONB in de database belanden, zetten we
 * hem hier expliciet om naar tekst — één plek waar dat gebeurt, in plaats van overal
 * een verrassing.
 */
export type SerializedGameState = Omit<GameState, 'rng'> & { readonly rng: string };

export function serializeState(state: GameState): SerializedGameState {
  return { ...state, rng: state.rng.state.toString() };
}

export function deserializeState(raw: SerializedGameState): GameState {
  return { ...raw, rng: { state: BigInt(raw.rng) } };
}

// ---------------------------------------------------------------- afgeleide vragen

/** De tegenstander van `seat`. */
export function opponentOf(seat: Seat): Seat {
  return seat === 0 ? 1 : 0;
}

export function playerAt(state: GameState, seat: Seat): PlayerState {
  return state.players[seat];
}

/** Grenst dit landschap aan het bouwwerk op `column`? */
export function isAdjacent(tile: LandscapeTile, buildingColumn: number): boolean {
  return tile.column === buildingColumn - 1 || tile.column === buildingColumn + 1;
}

/** De landschappen rond een bouwwerk (er zijn er hoogstens vier). */
export function landscapesAround(
  principality: Principality,
  buildingColumn: number,
): LandscapeTile[] {
  return principality.landscapes.filter((tile) => isAdjacent(tile, buildingColumn));
}

/** Aantal uitbreidingsplaatsen per zijde: een dorp heeft er één, een stad twee. */
export function slotsPerSide(kind: BuildingKind): number {
  return kind === 'dorp' ? 1 : 2;
}

/** Hoeveel uitbreidingen kunnen er nog bij dit bouwwerk? */
export function freeSlots(building: Building): { boven: number; onder: number } {
  const total = slotsPerSide(building.kind);
  return {
    boven: total - building.expansions.boven.length,
    onder: total - building.expansions.onder.length,
  };
}

/** Totaal aantal grondstoffen van een speler (alles wat op zijn landschappen ligt). */
export function totalResources(player: PlayerState): number {
  return player.principality.landscapes.reduce((sum, tile) => sum + tile.resources, 0);
}

/** Hoeveel van een bepaalde grondstof heeft deze speler? */
export function resourceCount(
  player: PlayerState,
  resource: Resource,
  resourceOf: (landscape: Landscape) => Resource,
): number {
  return player.principality.landscapes
    .filter((tile) => resourceOf(tile.landscape) === resource)
    .reduce((sum, tile) => sum + tile.resources, 0);
}
